import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';
import type { ContentItem } from '../types';
import '../styles/editor.css';

// ─── Types ───────────────────────────────────────────────────────────

interface SubmissionData {
  name: string;
  email: string;
  subject?: string;
  message: string;
  ip?: string;
  userAgent?: string;
  read?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────

/** Submission data comes from the public contact form — never trust its shape. */
function normalizeSubmission(raw: unknown): SubmissionData {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<SubmissionData>;
  return {
    name: typeof data.name === 'string' && data.name ? data.name : 'Unknown sender',
    email: typeof data.email === 'string' ? data.email : '',
    subject: typeof data.subject === 'string' ? data.subject : undefined,
    message: typeof data.message === 'string' ? data.message : '',
    read: !!data.read,
  };
}

function getSubmissionId(contentKey: string): string {
  const parts = contentKey.split('#');
  return parts.length > 1 ? parts[1] : contentKey;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Icons ───────────────────────────────────────────────────────────

const s = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const MailIcon = () => (
  <svg {...s} width="48" height="48" style={{ color: '#ccc' }}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const TrashIcon = () => (
  <svg {...s} width="14" height="14">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg {...s} width="16" height="16" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────

export default function MessageList() {
  const { siteId } = useParams<{ siteId: string }>();
  const { addToast } = useToast();

  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.listContent(siteId, 'submission');
      // Sort newest first by createdAt
      data.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleToggle = async (item: ContentItem) => {
    const id = getSubmissionId(item.contentKey);
    const isExpanding = expandedId !== id;
    setExpandedId(isExpanding ? id : null);

    // Mark as read when expanding an unread message
    const data = normalizeSubmission(item.data);
    if (isExpanding && !data.read && siteId) {
      try {
        await api.updateContent(siteId, 'submission', id, { ...item.data, read: true });
        setItems(prev =>
          prev.map(i =>
            i.contentKey === item.contentKey
              ? { ...i, data: { ...i.data, read: true } }
              : i,
          ),
        );
      } catch {
        // Non-critical — don't show error
      }
    }
  };

  const handleDelete = async (contentKey: string, name: string) => {
    if (!confirm(`Delete message from "${name}"?`)) return;
    if (!siteId) return;
    try {
      const id = getSubmissionId(contentKey);
      await api.deleteContent(siteId, 'submission', id);
      setItems(prev => prev.filter(i => i.contentKey !== contentKey));
      if (expandedId === id) setExpandedId(null);
      addToast('success', 'Message deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const unreadCount = items.filter(i => !normalizeSubmission(i.data).read).length;

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Messages</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Messages</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>
          Messages
          {unreadCount > 0 && (
            <span style={{
              fontSize: '0.6em',
              background: '#dc2626',
              color: '#fff',
              borderRadius: '10px',
              padding: '2px 8px',
              marginLeft: '8px',
              verticalAlign: 'middle',
              fontWeight: 600,
            }}>
              {unreadCount} new
            </span>
          )}
        </h1>
      </div>

      <p style={{ color: 'var(--color-gray-500)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
        Contact form submissions from your website visitors.
      </p>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><MailIcon /></div>
          <h2>No messages yet</h2>
          <p>When visitors submit your contact form, their messages will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--color-border)', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
          {items.map(item => {
            const data = normalizeSubmission(item.data);
            const id = getSubmissionId(item.contentKey);
            const isExpanded = expandedId === id;
            const isUnread = !data.read;

            return (
              <div key={item.contentKey} style={{ background: 'var(--color-bg)' }}>
                {/* Header row */}
                <div
                  onClick={() => handleToggle(item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    cursor: 'pointer',
                    transition: 'background 0.1s',
                    fontWeight: isUnread ? 600 : 400,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-gray-50, #f9fafb)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Unread dot */}
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: isUnread ? '#3b82f6' : 'transparent',
                    flexShrink: 0,
                  }} />

                  {/* Name & subject */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: isUnread ? 700 : 500, color: 'var(--color-text)' }}>
                        {data.name}
                      </span>
                      <span style={{ fontSize: '0.85em', color: 'var(--color-gray-500)' }}>
                        {data.email}
                      </span>
                    </div>
                    {data.subject && (
                      <div style={{ fontSize: '0.9em', color: 'var(--color-gray-600, #4b5563)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {data.subject}
                      </div>
                    )}
                    {!isExpanded && (
                      <div style={{ fontSize: '0.85em', color: 'var(--color-gray-400, #9ca3af)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {data.message.length > 120 ? data.message.substring(0, 120) + '…' : data.message}
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <span style={{ fontSize: '0.8em', color: 'var(--color-gray-400, #9ca3af)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatDate(item.createdAt)}
                  </span>

                  {/* Expand chevron */}
                  <span style={{ flexShrink: 0, color: 'var(--color-gray-400, #9ca3af)' }}>
                    <ChevronIcon open={isExpanded} />
                  </span>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ padding: '0 16px 16px 36px', borderTop: '1px solid var(--color-border)' }}>
                    <div style={{ padding: '16px 0' }}>
                      {data.subject && (
                        <div style={{ marginBottom: '12px' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.85em', color: 'var(--color-gray-500)' }}>Subject: </span>
                          <span>{data.subject}</span>
                        </div>
                      )}
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: 'var(--color-text)' }}>
                        {data.message}
                      </div>
                      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {data.email && (
                          <a
                            href={`mailto:${data.email}?subject=${encodeURIComponent(`Re: ${data.subject || 'Your message'}`)}`}
                            className="btn btn-primary btn-sm"
                            style={{ textDecoration: 'none' }}
                          >
                            Reply
                          </a>
                        )}
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={(e) => { e.stopPropagation(); handleDelete(item.contentKey, data.name); }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        >
                          <TrashIcon /> Delete
                        </button>
                        <span style={{ fontSize: '0.75em', color: 'var(--color-gray-400, #9ca3af)', marginLeft: 'auto' }}>
                          {new Date(item.createdAt).toLocaleString('en-AU', {
                            day: 'numeric', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
